import * as React from 'react';

// Gets DOM node's closest scrolling node

export const getScrollParent = (node) => {
    let offsetParent = node;
    while (offsetParent = offsetParent.offsetParent) {
        const overflowYVal = getComputedStyle(offsetParent, null).getPropertyValue('overflow-y');
        if (overflowYVal === 'auto' || overflowYVal === 'scroll') {
            return offsetParent;
        }
    }
    return window;
};

// Calculates DOM node's offset from target DOM node

export const offsetTill = (node, target) => {
    let current = node;
    let offset = 0;
    do {
        offset += current.offsetTop;
        current = current.offsetParent;
    } while (current && current !== target);
    return offset;
};

// Finds tallest parent to watch for height changes / mutations.

export const getTallestParent = (node) => {
    const roots = document.querySelectorAll('.epharmacy-root,.ecommerce-root');
    const r = roots[0] as HTMLDivElement;
    if (!node) {
        return r;
    }

    let tallestParent = node;
    let foundHeight = 0;
    if (r) {
        while (tallestParent !== r) {
            const currentNode = tallestParent.parentNode;
            const currentHeight = currentNode.getBoundingClientRect().height;
            if (currentHeight < foundHeight) {
                return tallestParent;
            } else {
                foundHeight = currentHeight;
                tallestParent = currentNode;
            }
        }
    }
    return r;
};

// Use setTimeout with Hooks in a declarative way.

/**
 * @see https://stackoverflow.com/a/59274757/3723993
 * @see https://overreacted.io/making-setinterval-declarative-with-react-hooks/
 */
export function useTimeout (callback: React.EffectCallback, delay: number | null): React.MutableRefObject<number | null> {
    const timeoutRef = React.useRef<number | null>(null);
    const callbackRef = React.useRef(callback);

    React.useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    React.useEffect(() => {
        if (typeof delay === 'number') {
            timeoutRef.current = window.setTimeout(() => callbackRef.current(), delay);
            return () => window.clearTimeout(timeoutRef.current || 0);
        }
        return () => null;
    }, [delay]);

    // In case you want to manually clear the timeout from the consuming component...:
    return timeoutRef;
}

// Keypress hook

export function useKeypress (key, action, deps) {
    React.useEffect(() => {
        function onKeyup (e) {
            if (e.key === key) {
                action();
            }
        }

        window.addEventListener('keydown', onKeyup);
        return () => window.removeEventListener('keydown', onKeyup);
    }, [...deps]);
}

// Previous

export function usePrevious (value) {
    const ref = React.useRef();
    React.useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
}

// Interval

export function useInterval (callback: () => void, delay: number | null) {
    const savedCallback = React.useRef(callback);

    React.useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    React.useEffect(() => {
        if (delay === null) {
            return () => {
            };
        }

        const id = setInterval(() => savedCallback.current(), delay);

        return () => clearInterval(id);
    }, [delay]);
}
